<template>
  <v-card
    :class="variant === 'desktop' ? 'agent-desktop-card' : 'agent-compact-card'"
    color="background"
    variant="elevated"
    elevation="4"
    @click="emitEdit"
  >
    <template v-if="variant === 'desktop'">
      <div class="agent-desktop-shell">
        <div class="agent-desktop-header">
          <div class="agent-desktop-header-main">
            <v-avatar size="48" rounded="lg" class="agent-avatar agent-icon-tile">
              <v-icon :icon="iconName" size="small"></v-icon>
            </v-avatar>
            <div class="agent-title-wrap">
              <div class="agent-title-row">
                <div class="agent-title">{{ agent.config.name }}</div>
                <v-chip
                  v-if="agent.config.publishedInfo?.isPublished"
                  size="small"
                  color="success"
                  variant="tonal"
                  class="agent-live-chip"
                >
                  LIVE
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <div class="agent-description">
          {{ agent.config.description }}
        </div>

        <div class="agent-chip-row">
          <v-chip size="small" variant="tonal" class="agent-chip">
            <v-icon icon="mdi-earth" size="x-small" start></v-icon>
            {{ modelChipLabel }}
          </v-chip>
          <v-chip size="small" variant="outlined" class="agent-chip">
            <v-icon icon="mdi-tools" size="x-small" start></v-icon>
            {{ toolsChipLabel }}
          </v-chip>
          <v-chip
            v-if="agent.config.embeddingOptions?.collections?.length"
            size="small"
            variant="outlined"
            class="agent-chip"
          >
            <v-icon icon="mdi-book-open-outline" size="x-small" start></v-icon>
            RAG
          </v-chip>
        </div>

        <div class="agent-footer">
          <div class="agent-footer-spacer"></div>
          <div class="agent-actions">
            <v-tooltip v-if="canShare" text="Share Agent" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  icon="mdi-share-variant"
                  size="small"
                  variant="plain"
                  @click.stop="showShare = true"
                />
              </template>
            </v-tooltip>
            <v-tooltip v-if="agent.config.publishedInfo?.publicUrl" text="Open in new window" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  icon="mdi-open-in-new"
                  size="small"
                  variant="plain"
                  :href="agent.config.publishedInfo.publicUrl"
                  target="_blank"
                  @click.stop
                />
              </template>
            </v-tooltip>
            <v-tooltip v-if="publishedSlug" text="Chat with Agent" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  icon="mdi-chat-outline"
                  size="small"
                  variant="plain"
                  @click.stop="goToChat"
                />
              </template>
            </v-tooltip>
            <v-tooltip text="Edit Agent" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  icon="mdi-pencil-outline"
                  size="small"
                  variant="plain"
                  @click.stop="emitEdit"
                />
              </template>
            </v-tooltip>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <v-card-item class="py-3">
        <template #prepend>
          <v-avatar size="40" rounded="lg" class="mr-3 agent-icon-tile">
            <v-icon :icon="iconName" size="small"></v-icon>
          </v-avatar>
        </template>
        <v-card-title class="d-flex align-center compact-title-row">
          <span class="compact-title">{{ agent.config.name }}</span>
          <v-chip v-if="agent.config.publishedInfo?.isPublished" color="success" size="x-small" class="ml-2">LIVE</v-chip>
        </v-card-title>
        <v-card-subtitle class="compact-subtitle">
          {{ agent.config.description }}
        </v-card-subtitle>
        <template #append>
          <div class="d-flex align-center">
            <v-tooltip v-if="agent.config.publishedInfo?.publicUrl" text="Open in new window" location="top">
              <template #activator="{ props }">
                <v-btn v-bind="props" color="primary" icon="mdi-open-in-new" size="small" variant="plain"
                  class="mr-1" :href="agent.config.publishedInfo.publicUrl" target="_blank" @click.stop />
              </template>
            </v-tooltip>
            <v-tooltip v-if="publishedSlug" text="Chat with Agent" location="top">
              <template #activator="{ props }">
                <v-btn v-bind="props" color="primary" icon="mdi-chat-outline" size="small" variant="plain"
                  class="mr-1" @click.stop="goToChat" />
              </template>
            </v-tooltip>
            <v-tooltip text="Edit Agent" location="top">
              <template #activator="{ props }">
                <v-btn v-bind="props" color="primary" icon="mdi-pencil-outline" size="small" variant="plain"
                  @click.stop="emitEdit" />
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-card-item>

      <v-card-text class="pt-0 pb-3">
        <div class="compact-chip-row">
          <v-chip size="small" variant="tonal" class="agent-chip">{{ modelChipLabel }}</v-chip>
          <v-chip size="small" variant="outlined" class="agent-chip">{{ toolsChipLabel }}</v-chip>
          <v-chip v-if="agent.config.embeddingOptions?.collections?.length" size="small" variant="outlined" class="agent-chip">RAG</v-chip>
        </div>
      </v-card-text>
    </template>

    <AgentShareDialog
      v-model="showShare"
      :ownerUsername="username"
      :slug="publishedSlug"
      :agentName="agent.config.name"
    />
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { UserAgentConfig } from '@/types/agents'
import { ModelConfig } from '@/types/models'
import { DEFAULT_AGENT_ICON, resolveAgentIcon } from '@/utils/agentIcon'
import { normalizeModelPricingLookupModel } from '@/utils/modelPricing'
import AgentShareDialog from '@/components/AgentShareDialog.vue'
import { getDashboardTabPath } from '@/router/dashboardTabs'

@Options({
  props: {
    agent: Object,
    variant: {
      type: String,
      default: 'compact'
    }
  },
  components: {
    AgentShareDialog
  },
  computed: {
    ...mapGetters(['modelConfigsById', 'username'])
  },
  emits: ['edit']
})
export default class AgentSummaryCard extends Vue {
  agent!: UserAgentConfig
  variant!: 'compact' | 'desktop'
  modelConfigsById!: Record<string, { config: ModelConfig }>
  username!: string
  showShare = false

  get iconName(): string {
    return resolveAgentIcon(this.agent?.config?.icon ?? DEFAULT_AGENT_ICON)
  }

  get publishedSlug(): string {
    return this.agent?.config?.publishedInfo?.slug || ''
  }

  get modelChipLabel(): string {
    const modelId = this.agent?.config?.model
    const model = modelId ? this.modelConfigsById?.[modelId]?.config : undefined
    const providerModel = model?.model || model?.name || modelId || 'No model'
    return normalizeModelPricingLookupModel(providerModel)
  }

  get toolCount(): number {
    return this.agent?.config?.toolOptions?.userTools?.length || 0
  }

  get functionCount(): number {
    const selected = this.agent?.config?.toolOptions?.selectedFunctions || {}
    return Object.values(selected).reduce((count, names) => count + (names?.length || 0), 0)
  }

  get toolsChipLabel(): string {
    const total = this.functionCount > 0 ? this.functionCount : this.toolCount
    return `${total} tools`
  }

  get canShare(): boolean {
    return !!this.username && !!this.publishedSlug
  }

  emitEdit(): void {
    this.$emit('edit', this.agent.config.name)
  }

  goToChat(): void {
    if (!this.publishedSlug) return
    this.$store.dispatch('setPendingChatAgent', { slug: this.publishedSlug })
    this.$store.dispatch('setTab', 'chat')
    const path = getDashboardTabPath('chat')
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }
}
</script>

<style scoped lang="scss">
.agent-desktop-card,
.agent-compact-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.agent-desktop-card {
  height: 100%;
  min-height: 214px;
}

.agent-desktop-shell {
  padding: 16px 16px 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.agent-desktop-header {
  display: flex;
  align-items: flex-start;
}

.agent-desktop-header-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.agent-avatar {
  flex-shrink: 0;
}

.agent-icon-tile {
  background-color: #1f1d16;
  color: #fff;
}

.agent-title-wrap {
  min-width: 0;
}

.agent-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.agent-title {
  font-size: 1.05rem;
  line-height: 1.15;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-live-chip {
  letter-spacing: 0.03em;
  flex-shrink: 0;
}

.agent-description {
  margin-top: 10px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.62);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.agent-chip-row,
.compact-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.agent-chip {
  font-size: 0.78rem;
}

.agent-footer {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  display: flex;
  justify-content: flex-end;
}

.agent-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.compact-title-row {
  min-width: 0;
}

.compact-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-subtitle {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
